import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  Field,
  Input,
  PageHeader,
  Select,
  Toolbar,
  formatMoney,
} from '@biovet/ui';
import { auth } from '../lib/actor.js';
import { MasterTable, Messages, useAction } from './document.js';
import { apiSend, useResource, type AdminWriteResult } from '../lib/api.js';
import { PAGE, letterhead, send } from '../lib/print.js';
import { company } from '../lib/reference-data.js';


interface PartyRow {
  id: string;
  code: string;
  name: string;
  legalName: string | null;
  isCustomer: boolean;
  isSupplier: boolean;
  origin: string;
  ntn: string | null;
  strn: string | null;
  cuin: string | null;
  taxId: string | null;
  isFiler: boolean;
  isSalesTaxRegistered: boolean;
  contactPerson: string | null;
  phone: string | null;
  mobile: string | null;
  email: string | null;
  website: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  province: string | null;
  postalCode: string | null;
  countryCode: string | null;
  currency: string;
  paymentTermsDays: number | null;
  creditLimit: string | null;
  isActive: boolean;
  receivableAccount: string | null;
  receivableAccountName: string | null;
  payableAccount: string | null;
  payableAccountName: string | null;
  ledgerLines: number;
  salesCount: number;
  purchaseCount: number;
}

interface CompanyLetterhead {
  name: string;
  legalName: string | null;
  ntn: string | null;
  strn: string | null;
  cuin: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  province: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  baseCurrency: string;
  reportFooter: string | null;
}

interface PartyRefs {
  parties: PartyRow[];
  company: CompanyLetterhead | null;
  currencies: Array<{ code: string; name: string }>;
  controls: Record<string, { code: string; name: string } | undefined>;
  origins: string[];
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
}

type Tab = 'SUPPLIER' | 'CUSTOMER';

function controlLabel(
  controls: Record<string, { code: string; name: string } | undefined> | undefined,
  key: string,
): string {
  const account = controls?.[key];
  return account ? `${account.name} ${account.code}` : 'no account mapped';
}

type Draft = Omit<
  PartyRow,
  | 'id'
  | 'receivableAccount'
  | 'receivableAccountName'
  | 'payableAccount'
  | 'payableAccountName'
  | 'ledgerLines'
  | 'salesCount'
  | 'purchaseCount'
  | 'paymentTermsDays'
  | 'creditLimit'
  | 'countryCode'
> & {
  mode: 'create' | 'edit';
  creditLimit: string;
  paymentTermsDays: string;
};

function blank(tab: Tab): Draft {
  return {
    mode: 'create',
    code: '',
    name: '',
    legalName: '',
    isCustomer: tab === 'CUSTOMER',
    isSupplier: tab === 'SUPPLIER',
    origin: 'LOCAL',
    ntn: '',
    strn: '',
    cuin: '',
    taxId: '',
    isFiler: true,
    isSalesTaxRegistered: false,
    contactPerson: '',
    phone: '',
    mobile: '',
    email: '',
    website: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    province: '',
    postalCode: '',
    currency: 'PKR',
    creditLimit: '',
    paymentTermsDays: '',
    isActive: true,
  };
}

const IDENTITY: Array<[keyof Draft, string, string?]> = [
  ['name', 'Company name'],
  ['legalName', 'Legal name', 'As registered, if it differs from the trading name'],
  ['ntn', 'NTN'],
  ['strn', 'STRN'],
  ['cuin', 'CUIN', 'Incorporation number — blank for a sole proprietor or an AOP'],
  ['taxId', 'Foreign tax ID', 'For a party registered outside Pakistan'],
  ['addressLine1', 'Address'],
  ['addressLine2', 'Address line 2'],
  ['city', 'City'],
  ['province', 'Province'],
  ['postalCode', 'Postal code'],
  ['contactPerson', 'Contact person'],
  ['phone', 'Phone'],
  ['mobile', 'Mobile'],
  ['email', 'Email'],
  ['website', 'Website'],
];

export function PartiesScreen() {
  const [tab, setTab] = useState<Tab>('SUPPLIER');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [search, setSearch] = useState('');
  const res = useResource<PartyRefs>('/masters/parties', {
    ...auth(),
    role: tab,
    include: includeInactive ? 'inactive' : undefined,
  });
  const { busy, notice, problem, run } = useAction(res.reload);
  const [draft, setDraft] = useState<Draft | null>(null);

  const parties = useMemo(() => {
    const all = res.data?.parties ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter((p) =>
      [p.code, p.name, p.legalName, p.ntn, p.city, p.receivableAccount, p.payableAccount]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [res.data, search]);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((d) => (d ? { ...d, [key]: value } : d));

  const save = () =>
    void run(async () => {
      if (!draft) throw new Error('No draft');
      const { mode, code, ...rest } = draft;
      const result =
        mode === 'create'
          ? await apiSend<AdminWriteResult>('POST', '/masters/parties', { ...rest, code }, auth())
          : await apiSend<AdminWriteResult>(
              'PATCH',
              `/masters/parties/${encodeURIComponent(code)}`,
              rest,
              auth(),
            );
      setDraft(null);
      return result;
    });

  const supplierTab = tab === 'SUPPLIER';

  return (
    <>
      <PageHeader
        breadcrumb={`Sales & Purchase / ${supplierTab ? 'Suppliers' : 'Customers'}`}
        title={supplierTab ? 'Suppliers' : 'Customers'}
        description={
          supplierTab
            ? 'Everyone you buy from, and what you owe each of them.'
            : 'Everyone you sell to, and what each of them owes you.'
        }
      />

      <Messages notice={notice} problem={problem} />

      {}
      {!draft && (
        <Card flush className="mb-3.5" data-noprint="1">
          <CardBody className="flex flex-wrap items-center gap-2.5">
            <Toolbar>
              <Button
                size="sm"
                variant={supplierTab ? 'primary' : 'ghost'}
                onClick={() => (setTab('SUPPLIER'), setDraft(null))}
              >
                Suppliers
              </Button>
              <Button
                size="sm"
                variant={!supplierTab ? 'primary' : 'ghost'}
                onClick={() => (setTab('CUSTOMER'), setDraft(null))}
              >
                Customers
              </Button>
            </Toolbar>
            <div className="min-w-[220px] flex-1">
              <Input
                value={search}
                placeholder="Code, name, NTN, city or account"
                aria-label="Search parties"
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </CardBody>
        </Card>
      )}

      {draft && (
        <Card flush className="mb-3.5" data-noprint="1">
          <CardHeader
            title={
              draft.mode === 'create'
                ? `New ${supplierTab ? 'supplier' : 'customer'}`
                : `Edit ${draft.code}`
            }
            subtitle="What is filled in here is what prints on the party sheet and heads their side of a purchase document."
            actions={
              <Toolbar>
                <Button size="sm" onClick={() => setDraft(null)}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  variant="primary"
                  loading={busy}
                  disabled={!draft.code || !draft.name || (!draft.isCustomer && !draft.isSupplier)}
                  onClick={save}
                >
                  Save
                </Button>
              </Toolbar>
            }
          />
          <CardBody className="flex flex-col gap-3.5">
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <Field label="Code" required hint="SUP-0004, CUS-0007 — fixed once documents exist">
                <Input
                  value={draft.code}
                  disabled={draft.mode === 'edit'}
                  onChange={(e) => set('code', e.target.value.toUpperCase())}
                />
              </Field>
              <Field
                label="Origin"
                hint={`Decides which payable a supplier settles against — ${controlLabel(res.data?.controls, 'AP_FOREIGN')} for a foreign one`}
              >
                <Select value={draft.origin} onChange={(e) => set('origin', e.target.value)}>
                  {(res.data?.origins ?? ['LOCAL', 'FOREIGN']).map((o) => (
                    <option key={o} value={o}>
                      {o[0]}
                      {o.slice(1).toLowerCase()}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Base currency" hint="What they invoice and settle in">
                <Select value={draft.currency} onChange={(e) => set('currency', e.target.value)}>
                  {(res.data?.currencies ?? []).map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.code} · {c.name}
                    </option>
                  ))}
                </Select>
              </Field>
              {}
              <Field
                label="Roles"
                hint="Both is legal — and opens two accounts. Un-ticking one closes its account while it is still empty."
              >
                <div className="flex flex-col gap-1.5 pt-1">
                  <Checkbox
                    checked={draft.isSupplier}
                    onChange={(e) => set('isSupplier', e.target.checked)}
                    label={`Supplier — payable under ${controlLabel(
                      res.data?.controls,
                      draft.origin === 'FOREIGN' ? 'AP_FOREIGN' : 'AP_LOCAL',
                    )}`}
                  />
                  <Checkbox
                    checked={draft.isCustomer}
                    onChange={(e) => set('isCustomer', e.target.checked)}
                    label={`Customer — receivable under ${controlLabel(res.data?.controls, 'AR_TRADE')}`}
                  />
                </div>
              </Field>
            </div>

            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              {IDENTITY.map(([key, label, hint]) => (
                <Field key={String(key)} label={label} hint={hint} required={key === 'name'}>
                  <Input
                    value={String(draft[key] ?? '')}
                    onChange={(e) => set(key, e.target.value as Draft[typeof key])}
                  />
                </Field>
              ))}
            </div>

            {}
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <Field
                label={`Credit limit${draft.isCustomer ? '' : ' (customers)'}`}
                hint="Blank is no limit. A sale that would take their ledger balance past this is refused."
              >
                <Input
                  inputMode="decimal"
                  placeholder="No limit"
                  value={draft.creditLimit}
                  onChange={(e) => set('creditLimit', e.target.value)}
                />
              </Field>
              <Field label="Payment terms" hint="Days from the invoice date — it sets the due date">
                <Input
                  inputMode="numeric"
                  placeholder="0 — due on invoice"
                  value={draft.paymentTermsDays}
                  onChange={(e) => set('paymentTermsDays', e.target.value)}
                />
              </Field>
            </div>

            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <Field label="Tax status">
                <div className="flex flex-col gap-1.5 pt-1">
                  <Checkbox
                    checked={draft.isFiler}
                    onChange={(e) => set('isFiler', e.target.checked)}
                    label="On the active taxpayer list — drives the withholding rate"
                  />
                  <Checkbox
                    checked={draft.isSalesTaxRegistered}
                    onChange={(e) => set('isSalesTaxRegistered', e.target.checked)}
                    label="Sales tax registered"
                  />
                </div>
              </Field>
              {draft.mode === 'edit' && (
                <Field label="Active">
                  <Checkbox
                    checked={draft.isActive}
                    onChange={(e) => set('isActive', e.target.checked)}
                    label="Offered on documents"
                  />
                </Field>
              )}
            </div>
          </CardBody>
        </Card>
      )}

      {!draft && (
        <MasterTable
          title={supplierTab ? 'Suppliers' : 'Customers'}
          subtitle="A party who has posted cannot be deleted — the balance in their account is real. Switch them off instead."
          loading={res.loading}
          error={res.error}
          empty={supplierTab ? 'No suppliers' : 'No customers'}
          onNew={res.data?.canCreate ? () => setDraft(blank(tab)) : undefined}
          newLabel={supplierTab ? 'New supplier' : 'New customer'}
          toggle={{
            on: includeInactive,
            onToggle: () => setIncludeInactive((v) => !v),
            label: 'Show inactive',
          }}
          columns={['Code', 'Name', 'Chart account', 'NTN', 'City', 'Documents', '']}
        >
          {parties.map((p) => (
            <tr key={p.id} className="bb-row-h border-b border-[var(--line-2)]">
              <td className="bb-mono px-[var(--pad)] text-[12.5px] font-medium">{p.code}</td>
              <td className="px-2.5 text-[13px]">
                {p.name}
                {p.origin === 'FOREIGN' && (
                  <Badge tone="info" className="ml-2">
                    Foreign
                  </Badge>
                )}
                {p.isCustomer && p.isSupplier && (
                  <Badge tone="warn" className="ml-2">
                    Both
                  </Badge>
                )}
                {!p.isActive && (
                  <Badge tone="mute" className="ml-2">
                    Inactive
                  </Badge>
                )}
              </td>
              <td className="bb-mono px-2.5 text-[11.5px] text-[var(--ink-2)]">
                {[supplierTab ? p.payableAccount : p.receivableAccount, supplierTab ? null : null]
                  .filter(Boolean)
                  .join('') || <span className="text-[var(--danger)]">no account</span>}
              </td>
              <td className="bb-mono px-2.5 text-right text-[11.5px]">{p.ntn ?? '—'}</td>
              <td className="px-2.5 text-right text-[12.5px]">{p.city ?? '—'}</td>
              <td className="bb-num px-2.5 text-right text-[12.5px]">
                {p.salesCount + p.purchaseCount || '—'}
              </td>
              <td className="px-[var(--pad)] text-right" data-noprint="1">
                <Toolbar>
                  <Button size="sm" onClick={() => printPartySheet(p)}>
                    Print
                  </Button>
                  <Button
                    size="sm"
                    disabled={!res.data?.canEdit}
                    onClick={() =>
                      setDraft({
                        mode: 'edit',
                        code: p.code,
                        name: p.name,
                        legalName: p.legalName ?? '',
                        isCustomer: p.isCustomer,
                        isSupplier: p.isSupplier,
                        origin: p.origin,
                        ntn: p.ntn ?? '',
                        strn: p.strn ?? '',
                        cuin: p.cuin ?? '',
                        taxId: p.taxId ?? '',
                        isFiler: p.isFiler,
                        isSalesTaxRegistered: p.isSalesTaxRegistered,
                        contactPerson: p.contactPerson ?? '',
                        phone: p.phone ?? '',
                        mobile: p.mobile ?? '',
                        email: p.email ?? '',
                        website: p.website ?? '',
                        addressLine1: p.addressLine1 ?? '',
                        addressLine2: p.addressLine2 ?? '',
                        city: p.city ?? '',
                        province: p.province ?? '',
                        postalCode: p.postalCode ?? '',
                        currency: p.currency,
                        creditLimit:
                          p.creditLimit && Number(p.creditLimit) > 0 ? p.creditLimit : '',
                        paymentTermsDays:
                          p.paymentTermsDays === null || p.paymentTermsDays === 0
                            ? ''
                            : String(p.paymentTermsDays),
                        isActive: p.isActive,
                      })
                    }
                  >
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    loading={busy}
                    disabled={
                      !res.data?.canDelete ||
                      p.ledgerLines > 0 ||
                      p.salesCount > 0 ||
                      p.purchaseCount > 0
                    }
                    onClick={() =>
                      void run(() =>
                        apiSend<AdminWriteResult>(
                          'DELETE',
                          `/masters/parties/${encodeURIComponent(p.code)}`,
                          undefined,
                          auth(),
                        ),
                      )
                    }
                  >
                    Delete
                  </Button>
                </Toolbar>
              </td>
            </tr>
          ))}
        </MasterTable>
      )}
    </>
  );
}


function esc(value: string | null | undefined): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function lines(...parts: Array<string | null | undefined>): string {
  return parts.filter(Boolean).map(esc).join(', ');
}

function printPartySheet(p: PartyRow) {
  const co = company();
  const field = (label: string, value: string | null | undefined) =>
    value ? `<tr><th>${esc(label)}</th><td>${esc(value)}</td></tr>` : '';

  const account = p.isSupplier ? p.payableAccount : p.receivableAccount;
  const accountName = p.isSupplier ? p.payableAccountName : p.receivableAccountName;
  const both =
    p.isCustomer && p.isSupplier
      ? `<tr><th>Also</th><td>${esc(
          p.isSupplier
            ? `Customer · ${p.receivableAccount ?? '—'}`
            : `Supplier · ${p.payableAccount ?? '—'}`,
        )}</td></tr>`
      : '';

  const doc = `<!doctype html>
<html><head><meta charset="utf-8"><title>${esc(p.code)} — ${esc(p.name)}</title>
<style>
  ${PAGE}
  * { box-sizing: border-box; }
  body { font: 12px/1.45 "IBM Plex Sans", system-ui, sans-serif; color: #000; margin: 0;
         background: #fff; }
  .head { display: flex; justify-content: space-between; align-items: flex-start;
          gap: 24px; border-bottom: 1.5px solid #000; padding-bottom: 8px; }
  .co { font-size: 16px; font-weight: 600; }
  .muted { color: #444; font-size: 11px; }
  h1 { font-size: 14px; margin: 16px 0 4px; text-transform: uppercase; letter-spacing: .08em; }
  .sub { font-size: 11px; color: #444; padding-bottom: 10px; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 14px; }
  th, td { border-bottom: 1px solid #bbb; padding: 5px 6px; vertical-align: top;
           text-align: left; font-size: 11.5px; }
  th { width: 32%; font-weight: 600; color: #333; }
  h2 { font-size: 11px; text-transform: uppercase; letter-spacing: .06em;
       margin: 14px 0 4px; border-bottom: 1.2px solid #000; padding-bottom: 3px; }
  .mono { font-variant-numeric: tabular-nums; }
  .sigs { display: flex; gap: 60px; padding-top: 48px; }
  .sig { flex: 1; border-top: 1px solid #000; padding-top: 4px; font-size: 11px; }
  .foot { padding-top: 18px; font-size: 10px; color: #444; }
</style></head>
<body>
  ${letterhead()}

  <h1>${esc(p.isSupplier ? 'Supplier record' : 'Customer record')}</h1>
  <div class="sub">${esc(p.code)} · ${esc(p.name)}${
    co?.baseCurrency ? ` · books kept in ${esc(co.baseCurrency)}` : ''
  }</div>

  <h2>Legal identity</h2>
  <table>
    ${field('Company name', p.name)}
    ${field('Legal name', p.legalName)}
    ${field('NTN', p.ntn)}
    ${field('STRN', p.strn)}
    ${field('CUIN', p.cuin)}
    ${field('Foreign tax ID', p.taxId)}
    ${field('Filer status', p.isFiler ? 'On the active taxpayer list' : 'Non-filer')}
    ${field('Sales tax', p.isSalesTaxRegistered ? 'Registered' : 'Not registered')}
  </table>

  <h2>Address and contact</h2>
  <table>
    ${field('Address', lines(p.addressLine1, p.addressLine2))}
    ${field('City', p.city)}
    ${field('Province', p.province)}
    ${field('Postal code', p.postalCode)}
    ${field('Country', p.countryCode)}
    ${field('Contact person', p.contactPerson)}
    ${field('Phone', p.phone)}
    ${field('Mobile', p.mobile)}
    ${field('Email', p.email)}
    ${field('Website', p.website)}
  </table>

  <h2>Ledger</h2>
  <table>
    ${field('Base currency', p.currency)}
    ${field('Origin', p.origin === 'FOREIGN' ? 'Foreign' : 'Local')}
    ${account ? `<tr><th>Chart account</th><td class="mono">${esc(account)}${accountName ? ` · ${esc(accountName)}` : ''}</td></tr>` : ''}
    ${both}
    ${field('Payment terms', p.paymentTermsDays === null ? null : `${p.paymentTermsDays} days from invoice`)}
    ${field('Credit limit', p.creditLimit && Number(p.creditLimit) > 0 ? formatMoney(p.creditLimit, { decimals: 0 }) : 'None set')}
    ${field('Documents on file', String(p.salesCount + p.purchaseCount))}
  </table>

  <div class="sigs">
    <div class="sig">&nbsp;<div class="muted">Verified by</div></div>
    <div class="sig">&nbsp;<div class="muted">Authorised signature</div></div>
  </div>

  <div class="foot">
    ${esc(co?.reportFooter ?? '')}
    Printed ${esc(new Date().toLocaleString())}.
  </div>
</body></html>`;

  send(doc);
}
